<div class="order-container">
  @if (isLoading) {
    <!-- Loading Skeleton -->
    <div class="order-skeleton">
      <!-- Header Skeleton -->
      <div class="skeleton-header">
        <div class="skeleton-title"></div>
        <div class="skeleton-actions">
          <div class="skeleton-btn"></div>
          <div class="skeleton-btn"></div>
        </div>
      </div>

      <!-- Items Skeleton -->
      <div class="skeleton-items">
        <div class="skeleton-section-title"></div>
        <div class="skeleton-item" *ngFor="let item of [1,2,3]">
          <div class="skeleton-item-image"></div>
          <div class="skeleton-item-content">
            <div class="skeleton-item-title"></div>
            <div class="skeleton-item-meta"></div>
          </div>
        </div>
      </div>

      <!-- Payment Skeleton -->
      <div class="skeleton-payment">
        <div class="skeleton-payment-item" *ngFor="let item of [1,2,3]">
          <div class="skeleton-payment-label"></div>
          <div class="skeleton-payment-amount"></div>
        </div>
      </div>
    </div>
  } @else {
    <!-- Order Header -->
    <div class="order-header">
      <div class="header-card">
        <div class="header-content">
          <div class="header-main">
            <h1 class="order-title">
              Order from {{ orderDetails?.barcode_details?.name }}
            </h1>
            <div class="header-actions">
              <button class="action-icon-btn" (click)="goToChat()" title="Chat with customer">
                <i class="fa fa-comment"></i>
              </button>
              <button class="action-icon-btn" (click)="settime(content4)" title="Set wait time">
                <i class="fa fa-clock"></i>
              </button>
              <button class="refresh-btn"
                      [class.loading]="isRefreshing"
                      (click)="refreshOrder()"
                      [disabled]="isRefreshing"
                      title="Refresh order">
                @if (isRefreshing) {
                  <div class="spinner"></div>
                } @else {
                  <i class="fa fa-sync-alt"></i>
                }
              </button>
            </div>
          </div>

          <!-- Order Status -->
          <div class="order-status">
            @if (!isOrderActive) {
              <span class="status-badge" [ngClass]="{
                'status-completed': orderDetails?.status === 'completed',
                'status-closed': orderDetails?.status === 'closed'
              }">
                <i class="fa fa-circle-check"></i>
                Order {{ orderDetails?.status | titlecase }}
              </span>
            } @else {
              <span class="status-badge status-active">
                <i class="fa fa-clock"></i>
                Order Active
              </span>
            }
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons" [class.disabled-actions]="!isOrderActive">
          <div class="primary-actions">
            <button class="btn btn-primary"
                    (click)="addItem()"
                    [disabled]="!isOrderActive">
              <i class="fa fa-plus"></i>
              Add Item
            </button>

            <button class="btn btn-primary"
                    (click)="transferButtonClick(transferModal)"
                    [disabled]="!isOrderActive">
              <i class="fa fa-exchange-alt"></i>
              Transfer
            </button>
          </div>

          <div class="secondary-actions">
            <button class="btn btn-secondary"
                    [routerLink]="['/orders/return-item/', id]"
                    [disabled]="!isOrderActive">
              <i class="fa fa-undo"></i>
              Return Item
            </button>

            @if (isOrderActive) {
              <button class="btn btn-danger" (click)="closeOrder()">
                <i class="fa fa-times-circle"></i>
                Close Order
              </button>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="order-content">
      <div class="content-grid">
        <!-- Order Items Section -->
        <div class="items-section">
          <div class="section-card">
            <div class="section-header">
              <h3 class="section-title">
                <i class="fa fa-list-ul"></i>
                Order Items
              </h3>
              <span class="items-count">{{ orderDetails?.order_items?.length || 0 }} items</span>
            </div>

            <div class="items-container">
              @for (item of orderDetails?.order_items; track item.id) {
                <div class="item-card">
                  <div class="item-image">
                    <img [src]="item.image" [alt]="item.inventory" loading="lazy">
                  </div>

                  <div class="item-content">
                    <div class="item-header">
                      <h4 class="item-name">{{ item.inventory }}</h4>
                      <div class="item-price">{{ item.amount | currency:'₦' }}</div>
                    </div>

                    <div class="item-meta">
                      <span class="quantity-badge">
                        <i class="fa fa-hashtag"></i>
                        {{ item.quantity }}
                      </span>
                      <span class="total-amount">
                        Total: {{ (item.amount * item.quantity) | currency:'₦' }}
                      </span>
                    </div>

                    @if (isOrderActive && !item.returned) {
                      <div class="item-actions">
                        <button class="btn-cancel"
                                (click)="cancelItem(item.id)"
                                [disabled]="isCancellingItem">
                          @if (isCancellingItem) {
                            <div class="spinner-sm"></div>
                          } @else {
                            <i class="fa fa-times"></i>
                          }
                          Cancel Item
                        </button>
                      </div>
                    }
                  </div>
                </div>
              } @empty {
                <div class="empty-state">
                  <div class="empty-icon">
                    <i class="fa fa-box-open"></i>
                  </div>
                  <h4 class="empty-title">No items in this order</h4>
                  <p class="empty-description">Add items to get started with this order</p>
                  @if (isOrderActive) {
                    <button class="btn btn-primary" (click)="addItem()">
                      <i class="fa fa-plus"></i>
                      Add First Item
                    </button>
                  }
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Customer Details Section -->
        @if (orderDetails?.user_details?.name ||
             orderDetails?.user_details?.phone ||
             orderDetails?.user_details?.email) {
          <div class="customer-section">
            <div class="section-card">
              <div class="section-header">
                <h3 class="section-title">
                  <i class="fa fa-user"></i>
                  Customer Details
                </h3>
              </div>

              <div class="customer-details">
                @if (orderDetails?.user_details?.name) {
                  <div class="detail-item">
                    <div class="detail-icon">
                      <i class="fa fa-user-circle"></i>
                    </div>
                    <div class="detail-content">
                      <label class="detail-label">Name</label>
                      <p class="detail-value">{{ orderDetails.user_details.name }}</p>
                    </div>
                  </div>
                }

                @if (orderDetails?.user_details?.phone) {
                  <div class="detail-item">
                    <div class="detail-icon">
                      <i class="fa fa-phone"></i>
                    </div>
                    <div class="detail-content">
                      <label class="detail-label">Phone Number</label>
                      <p class="detail-value">{{ orderDetails.user_details.phone }}</p>
                    </div>
                  </div>
                }

                @if (orderDetails?.user_details?.email) {
                  <div class="detail-item">
                    <div class="detail-icon">
                      <i class="fa fa-envelope"></i>
                    </div>
                    <div class="detail-content">
                      <label class="detail-label">Email Address</label>
                      <p class="detail-value">{{ orderDetails.user_details.email }}</p>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
      </div>

    <!-- Payment Summary -->
    <div class="payment-section">
      <div class="section-card">
        <div class="section-header">
          <h3 class="section-title">
            <i class="fa fa-credit-card"></i>
            Payment Summary
          </h3>
        </div>

        <div class="payment-grid">
          <div class="payment-item order-amount">
            <div class="payment-label">Order Amount</div>
            <div class="payment-value">{{ orderDetails?.total_amount | currency: '₦' }}</div>
          </div>

          <div class="payment-item amount-received">
            <div class="payment-label">Amount Received</div>
            <div class="payment-value">{{ getTotalReceived() | currency: '₦' }}</div>
          </div>

          <div class="payment-item remaining-amount"
               [ngClass]="{
                 'has-remaining': getRemainingAmount() > 0,
                 'fully-paid': getRemainingAmount() <= 0
               }">
            <div class="payment-label">Remaining Amount</div>
            <div class="payment-value" [class.text-danger]="getRemainingAmount() > 0">
              {{ getRemainingAmount() | currency: '₦' }}
            </div>
          </div>
        </div>

        <div class="payment-status">
          @if (orderDetails?.payment_status == 'pending') {
            <div class="status-badge status-pending">
              <i class="fa fa-clock"></i>
              Payment Unconfirmed
            </div>
          } @else {
            <div class="status-badge status-confirmed">
              <i class="fa fa-check-circle"></i>
              Payment Confirmed
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Bank Accounts Section -->
    @if (orderDetails?.bank_accounts?.length) {
      <div class="bank-section">
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fa fa-building-columns"></i>
              Generated Bank Accounts
            </h3>
          </div>

          <div class="bank-grid">
            @for (account of getSortedBankAccounts(); track account.id) {
              <div class="bank-card" [class.paid-account]="account.payment_status === 'paid'">
                <div class="bank-info">
                  <div class="bank-icon">
                    <i class="fa" [ngClass]="account.payment_status === 'paid' ? 'fa-check-circle' : 'fa-building-columns'"></i>
                  </div>
                  <div class="bank-details">
                    <h5 class="bank-name">{{ account.bank_name }}</h5>
                    <p class="account-number">{{ account.account_number }}</p>
                    <p class="account-amount">{{ account.amount | currency:'₦' }}</p>
                  </div>
                </div>
                <div class="bank-status">
                  @if (account.payment_status === 'paid') {
                    <span class="badge badge-success">Paid</span>
                  } @else if (account.is_expired) {
                    <span class="badge badge-danger">Expired</span>
                  } @else {
                    <span class="badge badge-primary">Active</span>
                    <button class="btn-view" (click)="viewAccountDetails(account)">
                      <i class="fa fa-eye"></i>
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- Transaction History -->
    @if (orderDetails?.transactionLog?.length) {
      <div class="transaction-section">
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fa fa-history"></i>
              Payment History
            </h3>
          </div>

          <div class="transaction-table">
            <div class="table-header">
              <div class="header-cell">Amount</div>
              <div class="header-cell">Method</div>
              <div class="header-cell">Reference</div>
              <div class="header-cell">Date</div>
            </div>

            @for (transaction of orderDetails?.transactionLog; track transaction.reference) {
              <div class="table-row">
                <div class="table-cell">{{ transaction.amount | currency: '₦' }}</div>
                <div class="table-cell">{{ transaction.payment_method }}</div>
                <div class="table-cell">{{ transaction.reference }}</div>
                <div class="table-cell">{{ transaction.received_at | date:'short' }}</div>
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- Bottom Actions -->
    <div class="bottom-actions">
      <div class="actions-card">
        <div class="actions-grid">
          <div class="action-group">
            @if (orderDetails?.status == 'delivered' || orderDetails?.status == 'completed') {
              <button class="btn btn-success btn-disabled">
                <i class="fa fa-check-circle"></i>
                Order Delivered
              </button>
            } @else {
              <button class="btn btn-primary"
                      (click)="markOrder('delivered')"
                      [disabled]="!isOrderActive || isMarkingDelivered">
                @if (isMarkingDelivered) {
                  <div class="spinner-sm"></div>
                } @else {
                  <i class="fa fa-truck"></i>
                }
                Mark as Delivered
              </button>
            }
          </div>

          <div class="action-group">
            @if (orderDetails?.payment_status == 'approved') {
              <button class="btn btn-success btn-disabled">
                <i class="fa fa-check-circle"></i>
                Payment Successful
              </button>
            } @else {
              <button class="btn btn-danger"
                      (click)="confirmPayment()"
                      [disabled]="!isOrderActive || isProcessingPayment">
                @if (isProcessingPayment) {
                  <div class="spinner-sm"></div>
                } @else {
                  <i class="fa fa-credit-card"></i>
                }
                Process Payment
              </button>
            }
          </div>
        </div>
      </div>
    </div>
  }
</div>

<ng-template let-modal #content>
  <div class="modal-body">
    <div class="text-center p-3">
      <h3 class="table-name">Complete Order</h3>
      <p>
        Are you sure you want to mark this order as completed?
      </p>

      <div class="mt-3">
        <button class="btn btn-primary btn-block complete-order-btn" (click)="markOrder('completed')">Yes,
          Confirm</button>
      </div>
      <div class="mt-2">
        <button class="btn btn-secondary btn-block complete-order-btn-cancel"
          (click)="modal.dismiss('Cross click')">Cancel</button>
      </div>
    </div>
  </div>
</ng-template>

<ng-template let-modal #content2>
  <div class="modal-body relative">
    <div class="absolute">
      <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')"></button>
    </div>
    <div class="text-center p-3">
      <h3>Conflict Resolution</h3>
      <p>
        Why are you notifying the management of a conflict resolution?
      </p>

      <div class="text-start">
        <ul class="list-group list-group-flush">
          <li class="list-group-item">
            <input class="form-check-input me-1" type="checkbox" value="" id="firstCheckbox">
            <label class="form-check-label" for="firstCheckbox">Payment Conflict </label>
          </li>
          <li class="list-group-item">
            <input class="form-check-input me-1" type="checkbox" value="" id="secondCheckbox">
            <label class="form-check-label" for="secondCheckbox">Order Conflict </label>
          </li>
          <li class="list-group-item">
            <input class="form-check-input me-1" type="checkbox" value="" id="thirdCheckbox">
            <label class="form-check-label" for="thirdCheckbox">Rude Customer Behavior</label>
          </li>
          <li class="list-group-item">
            <input class="form-check-input me-1" type="checkbox" value="" id="fourthCheckbox">
            <label class="form-check-label" for="fourthCheckbox">Other Conflict </label>
          </li>
        </ul>
      </div>

      <div class="mt-3">
        <button class="btn btn-primary btn-block" (click)="confirmResolution()">Done</button>
      </div>
      <!-- <div class="mt-2">
        <button class="btn btn-secondary btn-block" (click)="modal.dismiss('Cross click')">Cancel</button>
      </div> -->
    </div>
  </div>
</ng-template>


<ng-template let-modal #content3>
  <div class="modal-body p-5 relative">
    <div class="absolute">
      <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')"></button>
    </div>
    <div class="text-center">
      <h3>Customer Buzz</h3>
      <p>
        Barcode 007 - a customer you are attending to just buzzed you for reason <b>Order Delay”</b>
      </p>
    </div>

    <div class="text-center mt-3">
      <button class="btn btn-primary btn-lg btn-block">Done</button>
      <button class="btn btn-outline-secondary btn-lg btn-block mt-3 fw-bold">Chat Customer</button>
    </div>
  </div>
</ng-template>


<ng-template let-modal #content4>
  <div class="modal-body p-4 relative">
    <div class="absolute">
      <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')"></button>
    </div>
    <div class="text-center">
      <h3 class="table-name">Wait Time</h3>
      <p>
        Set a wait time
      </p>
    </div>
    <div class="d-flex align-items-center justify-content-center">
      <div class="minus">
        <button class="btn btn-primary" (click)="minus()">-</button>
      </div>
      <div class="minutes border border-1">
        {{ minutes }}
      </div>
      <div class="plus">
        <button class="btn btn-primary" (click)="plus()">+</button>
      </div>
    </div>
    <div class="mt-2 text-center">
      <p>Minutes</p>
    </div>

    <div class="text-center mt-3">
      <button class="btn btn-primary btn-lg btn-block" (click)="TimeSet()">Done</button>
    </div>
  </div>
</ng-template>
<ng-template let-modal #transferModal>
  <div class="modal-body p-4 relative">
    <div class="absolute">
      <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')"></button>
    </div>
    <div class="text-center">
      <h3 class="table-name">Transfer Order</h3>
      <p>Select a waiter to transfer this order to</p>
    </div>

    <form [formGroup]="transferForm" (ngSubmit)="transferOrder()">
      <div class="form-group mb-3">
        <label class="form-label">Select Waiter</label>
        <select class="form-select" formControlName="waiter_id">
          <option value="">Choose waiter</option>
          @for (waiter of activeWaiters; track waiter.id) {
          <option [value]="waiter.id">{{ waiter.first_name + ' ' + waiter.last_name }}</option>
          }
        </select>
      </div>

      <div class="form-group mb-4">
        <label class="form-label">Transfer Reason</label>
        <textarea class="form-control" rows="3" formControlName="notes"
          placeholder="Enter reason for transfer"></textarea>
      </div>

      <div class="text-center mt-3">
        <button class="btn btn-primary btn-lg btn-block complete-order-btn" type="submit"
          [disabled]="transferForm.invalid || isTransferLoading">
          <span *ngIf="isTransferLoading" class="spinner-border spinner-border-sm me-2" role="status"
            aria-hidden="true"></span>
          {{ isTransferLoading ? 'Transferring...' : 'Transfer Order' }}
        </button>
        <button class="btn btn-secondary btn-lg btn-block complete-order-btn-cancel mt-2" type="button"
          (click)="modal.dismiss('Cancel')">
          Cancel
        </button>
      </div>
    </form>
  </div>
</ng-template>

<!-- Account Details Modal -->
<ng-template #accountDetailsModal let-modal>
  <div class="modal-body p-4">
    <div class="text-end">
      <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
    </div>
    <div class="text-center mb-4">
      <h3 class="table-name">Transfer Details</h3>
      <p>Please make your payment using the account details below</p>
    </div>

    <div class="transfer-details">
      <div class="detail-item">
        <p class="label">Account Name</p>
        <h5 class="value">{{selectedAccount?.account_name}}</h5>
      </div>

      <div class="detail-item">
        <p class="label">Account Number</p>
        <h5 class="value">{{selectedAccount?.account_number}}</h5>
      </div>

      <div class="detail-item">
        <p class="label">Bank Name</p>
        <h5 class="value">{{selectedAccount?.bank_name}}</h5>
      </div>

      <div class="detail-item">
        <p class="label">Amount</p>
        <h5 class="value">{{selectedAccount?.amount | currency:'₦'}}</h5>
      </div>

      <div class="detail-item">
        <p class="label">Expires At</p>
        <h5 class="value">{{selectedAccount?.expires_at | date:'medium'}}</h5>
      </div>
    </div>
  </div>
</ng-template>
